import _unionBy from 'lodash/unionBy'
import _differenceBy from 'lodash/differenceBy'
import _get from 'lodash/get'
import _some from 'lodash/some'
import _cloneDeep from 'lodash/cloneDeep'
import _merge from 'lodash/merge'
import _findIndex from 'lodash/findIndex'
import randomstring from 'randomstring'

import { firestore, storage, firestoreConstructors } from '../../plugins/firebase'

import {store} from '../../store'
import { defaultRoles, NOTIFICATIONS_TYPES, EQUIPMENT_TYPE, FIRESTORE_CAMPAIGN_PRE_DATA_STEPS, HUBSPOT_ONBOARDING_STATES, DEFAULT_FIELD_SOURCE, WEBHOOKS_PATHS } from '../../constants/const' // eslint-disable-line no-unused-vars -- used in pluviometricChart.fieldSource
import { sortByName, parseDevice, getBearing, getLatestClonedDevices, getLatestClonedFields } from '../helpers'

import {addCropToStoreDevices, updateCropIntoStoreCrops, updateCropIntoStoreFields, addCropToStoreCrop, addCropToStoreField, updateCropIntoStoreDevices} from './storeOperations'
import { cleanDeep, getDataChanges } from '../helpers'

/**
 * @typedef {Object} userNotification
 * @property {string} id
 * @property {string} name
 */

/**
 * It maps a device's notifications users to a list of permissions for each one.
 * @param {{
 *  failure: Array<userNotification>
 *  off: Array<userNotification>
 *  position: Array<userNotification>
 *  pressure: Array<userNotification>
 * }} notifications device's notifications users.
 * @param {Array<userNotification>} allUsers all the devices's users, even without any notification.
 * @returns {Array<{
 *  id: string,
 *  name: string,
 *  permissions: {
 *    failure: boolean
 *    off: boolean
 *    position: boolean
 *    pressure: boolean
 *  }
 * }>} list of users permissions.
 */
function mapNotificationsToPermissions (notifications = {}, allUsers = []) {
  const userPermissions = []

  NOTIFICATIONS_TYPES.forEach(type => {
    const users = notifications[type] || []
    users.forEach(user => {
      const index = userPermissions.findIndex(element => element.id === user.id)

      if (index >= 0) { // user is already added to the array
        const userPermission = userPermissions[index]
        userPermission.permissions[type] = true
      } else { // user not added yet
        const userPermission = {
          id: user.id,
          name: user.name,
          permissions: {
            failure: false,
            pressure: false,
            off: false,
            position: false
          }
        }
        userPermission.permissions[type] = true
        userPermissions.push(userPermission)
      }
    })
  })

  // adds all device's users that do not have any notification set up yet
  allUsers.forEach(user => {
    const isUserAlreadyAdded = !!userPermissions.find(element => element.id === user.id)

    if (!isUserAlreadyAdded) {
      userPermissions.push({
        id: user.id,
        name: user.name,
        permissions: {
          failure: false,
          pressure: false,
          off: false,
          position: false
        }
      })
    }
  })

  return userPermissions
}

/**
 * @typedef {Object} userData
 * @property {string} id
 * @property {Array<string>} channels
 * @property {boolean} isAssigned
 * @property {string} email
 * @property {string} phone
 * @property {string} name
 */

/**
 * It updates the users' devices and the users assigned to devices. If an user is
 * assigned to a device, he/she is added to it, and viceversa. When an user is unassigned
 * he/she is removed from the device, and viceversa.
 * @param {userData[]} users list of users to be assigned/unassigned to/from `devices`.
 * @param {Array<{id: string, isAssigned: boolean}>} devices list of devices to be
 * assigned/unassigned to/from `users`.
 * @param {{ appendDevices: boolean, appendUsers: boolean }} append flag object that
 * indicates if the users should be appended to the device or replace all current users.
 * The same applies for the devices.
 * @param {boolean} updateNotifications flag that indicates if the devices' notifications
 * should be updated or not.
 * @param {string} campaignId id of the campaign to be used.
 * @returns {Promise<Array<{ id: string, users: userData[] }>} a list of the updated
 * users assigned to each device.
 *
 * `NOTE`: Although the function receives two arrays, to work properly it should always
 * receive 1 user and 1-N devices, or 1 device and 1-N users. It does not work with
 * N users and N devices. The list with only one element should always have that element's
 * `isAssigned` equal to true.
 */
export async function assignUsersToDevices (users = [], devices = [], append = {}, updateNotifications, campaignId) {
  const { appendUsers, appendDevices } = append

  const devicesRefs = []
  const devicesIds = []
  let updatedDevices = []

  const usersData = []
  const userIds = []

  try {
    const db = await firestore

    users.forEach(user => {
      userIds.push(user.id)

      if (user.isAssigned) {
        usersData.push({
          id: user.id,
          channels: user.channels,
          email: user.email || null,
          name: user.name,
          phone: user.phone
        })
      }
    })

    // appends, removes or replaces users in devices
    updatedDevices = await Promise.all(
      devices.map(async (device) => {
        const ref = db.doc(`app/v1/devices/${device.id}`)
        const doc = await ref.get()
        const currentUsers = doc.data().users.data
        const currentNotifications = _get(doc.data(), 'users.permissions.notifications', {})

        const newNotifications = {}
        let updatedUsers = []
        let permissions = []

        devicesIds.push(device.id)

        if (device.isAssigned) {
          devicesRefs.push(ref)
          updatedUsers = appendUsers ? _unionBy(currentUsers, usersData, 'id') : usersData
          if (updateNotifications) {
            NOTIFICATIONS_TYPES.forEach(type => {
              const users = currentNotifications[type] || []
              newNotifications[type] = users.filter(user => !!usersData.find(element => element.id === user.id))
            })
          }

          await ref.update({
            'users.data': updatedUsers,
            'users.permissions.notifications': updateNotifications
              ? newNotifications
              : currentNotifications
          })

          permissions = mapNotificationsToPermissions(
            updateNotifications ? newNotifications : currentNotifications,
            updatedUsers
          )
        } else {
          updatedUsers = currentUsers.filter(user => !userIds.includes(user.id))
          if (updatedUsers.length !== currentUsers.length) {
            NOTIFICATIONS_TYPES.forEach(type => {
              const users = currentNotifications[type] || []
              newNotifications[type] = users.filter(user => !userIds.includes(user.id))
            })

            await ref.update({
              'users.data': updatedUsers,
              'users.permissions.notifications': newNotifications
            })

            permissions = mapNotificationsToPermissions(newNotifications, updatedUsers)
          } else {
            permissions = mapNotificationsToPermissions(currentNotifications, updatedUsers)
          }
        }

        return { id: device.id, users: updatedUsers, permissions }
      })
    )

    // appends, removes or replaces devices in users
    users.map(async (user) => {
      const userCampaignRef = db.doc(`app/v1/users/${user.id}/campaigns/${campaignId ||store.getters.activeCampaign}`)
      const userCampaignDoc = await userCampaignRef.get()
      const currentDevices = userCampaignDoc.data().devices

      if (user.isAssigned) {
        await userCampaignRef.update({
          devices: appendDevices ? _unionBy(currentDevices, devicesRefs, 'id') : devicesRefs
        })
      } else {
        const filteredDevices = currentDevices.filter(device => !devicesIds.includes(device.id))
        if (filteredDevices.length !== currentDevices.length) await userCampaignRef.update({ devices: filteredDevices })
      }
    })

    return updatedDevices
  } catch (error) {
    console.error('Error while assigning users to devices:', error)
    throw error
  }
}

export async function upsertPluviometricChartData (oldChartData, chartData, chartId) {
  try {
    const db = await firestore

    if (chartId) {
      const chartRef = db.collection('app/v1/pluviometricCharts').doc(chartId)
      await chartRef.update(chartData)
      await addChangeLog(chartRef, oldChartData, chartData)
    } else {
      const chartRef = await db.collection('app/v1/pluviometricCharts').add(chartData)
      await addChangeLog(chartRef, {}, chartData)
      chartId = chartRef.id
    }

    return {chartId}
  } catch (err) {
    console.error('ERROR_UPSERT_CHART:', err)
    throw err
  }
}

/**
 * It updates the list of users assigned to each device in the local store.
 * @param {Array<{ id: string, users: userData[]  }>} updatedDevices list of
 * updated users assigned to each device in the back-end.
 */
function updateUsersAssignmentsInStore (updatedDevices = []) {
  const updatedStoreDevices = store.getters.devices.map(device => {
    const updatedDevice = updatedDevices.find(element => element.id === device.id)

    return updatedDevice
      ? {
        ...device,
        users: updatedDevice.users.sort(sortByName),
        notifications: updatedDevice.permissions
      }
      : device
  })
  store.commit('setDevices', updatedStoreDevices)
}

/**
 * It adds a new user to the back-end, storing its data and profile picture
 * and adding the user to the organization.
 * @param {Object} userData data of the new user.
 * @param {Array<Object>} assignedDevices list of devices to be assigned to
 * the user.
 * @param {Array<Object>} devicesPermissions list of devices assigned with permissions to notify.
 * @param {string} campaignId id of the campaign to be used.
 */
export async function insertNewUser (userData, assignedDevices = [], devicesPermissions = [], campaignId) {
  let profilePic = ''

  const lastUpdateDate = new Date()

  try {
    const db = await firestore

    // transaction to create user and add it to the organization
    const roles = { ...defaultRoles }
    roles[userData.role] = true

    const organizationId = userData.organization.id

    const newUserData = {
      phone: userData.phone,
      notificationTokens: [],
      notifications: {},
      name: userData.name,
      lastName: userData.lastName,
      isAdmin: userData.role === 'admin',
      channels: userData.channels,
      organization: db.doc(`app/v1/organizations/${organizationId}`),
      settings: {
        preferredLanguage: store.getters.language || 'es',
        notificationsPeriod: userData.notificationsPeriod
      },
      lastUpdateDate,
      isNewUser: true,
      audits: userData.audits
    }
    if (userData.email) {
      newUserData.email = userData.email
    }
    const newUserCampaignData = {
      devices: [],
      roles,
      reports: !!userData.reports,
      editDataPermission: !!userData.editDataPermission,
      editCampaignDataPermission: !!userData.editCampaignDataPermission
    }

    const {userId, profilePicPath} = await db.runTransaction(async (transaction) => {
      let profilePicPath = ''
      const userRef = db.collection('app/v1/users').doc()
      await transaction.set(userRef, newUserData)
      const userId = userRef.id // After the transaction.set the userRef gets updated
      const userCampaignId = campaignId || store.getters.activeCampaign
      const userCampaignRef = db.collection('app/v1/users').doc(userId).collection('campaigns').doc(userCampaignId)
      await transaction.set(userCampaignRef, newUserCampaignData)
      await addChangeLog(userRef, {}, newUserData, transaction, userCampaignId)
      await addChangeLog(userCampaignRef, {}, newUserCampaignData, transaction, userCampaignId)

      if (userData.profilePic) {
        const fileExtension = userData.profilePicFile.name.split('.').pop() || ''
        profilePicPath = `/profilePictures/${userId}.${fileExtension}`
        await transaction.update(userRef, {photoUrl: profilePicPath})
      }

      const campaignRef = await db.doc(`app/v1/campaigns/${userCampaignId}`).get()
      const { users } = campaignRef.data()
      transaction.update(db.doc(`app/v1/campaigns/${userCampaignId}`), {
        users: [...users, userRef]
      })

      return {userId, profilePicPath}
    })

    // stores profile pic
    if (userData.profilePic) {
      const profilePicRef = storage.ref().child(profilePicPath)
      await profilePicRef.put(userData.profilePicFile)
      profilePic = await profilePicRef.getDownloadURL()
    }

    // assigns user to devices
    const updatedDevices = await assignUsersToDevices(
      [{ ...newUserData, id: userId, isAssigned: true }],
      assignedDevices,
      // since the devices already exist, the user should be appended or removed
      { appendUsers: true },
      false,
      campaignId
    )

    // if the user is not from the Admin user org, it will not update the store
    if (store.getters.userOrganization.id === userData.organization.id) {
      const devices = []

      assignedDevices.forEach(device => {
        if (device.isAssigned) devices.push(device.id)
      })

      const storeUserData = {
        id: userId,
        name: userData.name,
        lastName: userData.lastName,
        phone: userData.phone,
        reports: userData.reports || false,
        devices,
        role: userData.role,
        editDataPermission: userData.editDataPermission,
        photoUrl: profilePicPath || '',
        profilePic,
        channels: userData.channels,
        settings: newUserData.settings,
        audits: userData.audits
      }

      // stores user in local store
      let users = [...store.getters.organizationUsers, storeUserData]
      users = users.sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0))
      store.commit('setOrganizationUsers', users)

      updateUsersAssignmentsInStore(updatedDevices)

      // assignUserToMultipleDevicePermissions
      if (devicesPermissions.length) {
        await assignUserToMultipleDevicePermissions(newUserData.name, userId, devicesPermissions)
      }
    }
    return userId
  } catch (error) {
    console.error('Error while saving user data:', error)
    throw error
  }
}

/**
 * Creates a minimal user document for field-survey technician assignment only.
 * No fieldSurveys, no campaigns, no roles, no organization, no insertNewUser side effects.
 * @param {Object} params
 * @param {string} params.name
 * @param {string} params.lastName
 * @param {string} params.phone
 * @param {string} params.email
 * @returns {Promise<string>} new user id
 */
export async function createFieldSurveyTechnicianUser ({ name, lastName, phone, email }) {
  const response = await fetch(`${process.env.WEBHOOKS_URL}${WEBHOOKS_PATHS.PHONE_EXISTS}?number=${encodeURIComponent(phone)}`, {
    headers: {
      authorization: process.env.WEBHOOKS_BASIC_TOKEN
    }
  })
  const resData = await response.json()
  if (resData.exists !== false) {
    const err = new Error('PHONE_EXISTS')
    err.code = 'PHONE_EXISTS'
    throw err
  }

  const db = await firestore
  const userRef = db.collection('app/v1/users').doc()
  await userRef.set({
    name,
    lastName,
    phone,
    email,
    audits: false,
    reports: false,
    editDataPermission: false,
    editCampaignDataPermission: false,
    channels: ['push', 'whatsapp', 'sms'],
    notificationsPeriod: {
      startTime: '00:00',
      endTime: '23:59'
    },
    newTechnician: true
  })
  return userRef.id
}

/**
 * It updates an existing user data in the back-end, storing its profile picture if it
 * was updated too.
 * @param {Object} userData updated user data.
 * @param {Array<Object>} assignedDevices updated list of devices to be assigned to
 * the user.
 */
export async function updateUser (userData, assignedDevices = [], devicesPermissions = []) {
  let fileExtension = ''
  let profilePicPath = ''

  const lastUpdateDate = new Date()
  const devices = assignedDevices.filter(({ isAssigned }) => isAssigned)
  const roles = { ...defaultRoles }
  roles[userData.role] = true

  const defaultNotificationHours = {
    startHour: '00:00',
    endHour: '23:59'
  }

  try {
    const db = await firestore

    const newUserData = {
      phone: userData.phone,
      name: userData.name,
      lastName: userData.lastName,
      isAdmin: userData.role === 'admin',
      channels: userData.channels,
      'settings.notificationsPeriod': userData.settings.notificationsPeriod || defaultNotificationHours,
      lastUpdateDate,
      audits: userData.audits
    }
    const newUserCampaignData = {
      roles,
      reports: !!userData.reports,
      editDataPermission: !!userData.editDataPermission,
      devices: devices.map(device => db.collection('app/v1/devices').doc(device.id))
    }

    if (userData.email) newUserData.email = userData.email

    // updates user in back-end
    if (userData.profilePicFile) {
      fileExtension = userData.profilePicFile.name.split('.').pop() || ''
      profilePicPath = `/profilePictures/${userData.id}.${fileExtension}`
      newUserData.photoUrl = profilePicPath
    }

    const userRef = db.collection('app/v1/users').doc(userData.id)
    const userDoc = await userRef.get()
    const oldUserData = userDoc.exists ? userDoc.data() : {}
    await userRef.update(newUserData)
    const campaignId = store.getters.activeCampaign
    const userCampaignRef = db.collection('app/v1/users').doc(userData.id).collection('campaigns').doc(campaignId)
    const userCampaignDoc = await userCampaignRef.get()
    const oldUserCampaignData = userCampaignDoc.exists ? userCampaignDoc.data() : {}
    await addChangeLog(userRef, oldUserData, newUserData, null, campaignId)
    await userCampaignRef.update(newUserCampaignData)
    await addChangeLog(userCampaignRef, oldUserCampaignData, newUserCampaignData, null, campaignId)

    const storeUserData = {
      name: userData.name,
      lastName: userData.lastName,
      email: userData.email,
      phone: userData.phone,
      reports: userData.reports,
      devices: devices.map(device => device.id),
      role: userData.role,
      editDataPermission: userData.editDataPermission,
      channels: userData.channels,
      audits: userData.audits
    }

    // stores profile pic
    if (userData.profilePicFile) {
      const profilePicRef = storage.ref().child(profilePicPath)
      await profilePicRef.put(userData.profilePicFile)
      storeUserData.profilePic = await profilePicRef.getDownloadURL()
      storeUserData.photoUrl = profilePicPath
    }

    // assigns user to devices
    const updatedDevices = await assignUsersToDevices(
      [{ ...newUserData, id: userData.id, isAssigned: true }],
      assignedDevices,
      // since the devices already exist, the user should be appended or removed
      { appendUsers: true }
    )

    // stores user in local store
    const organizationUserIndex = store.getters.organizationUsers.findIndex(user => user.id === userData.id)
    const nameHasChanged = store.getters.organizationUsers[organizationUserIndex].name !== userData.name

    let organizationUsers = [...store.getters.organizationUsers]

    organizationUsers[organizationUserIndex] = {
      ...organizationUsers[organizationUserIndex],
      ...storeUserData,
      settings: {
        ...organizationUsers[organizationUserIndex].settings,
        notificationsPeriod: userData.settings.notificationsPeriod
      }
    }

    if (nameHasChanged) organizationUsers = organizationUsers.sort(sortByName)
    store.commit('setOrganizationUsers', organizationUsers)

    let users = [...store.getters.users]
    const userIndex = store.getters.users.findIndex(user => user.id === userData.id)

    users[userIndex] = {
      ...users[userIndex],
      ...storeUserData,
      settings: {
        ...users[userIndex].settings,
        notificationsPeriod: userData.settings.notificationsPeriod
      }
    }
    store.commit('setUsers', users)

    updateUsersAssignmentsInStore(updatedDevices)

    // assignUserToMultipleDevicePermissions
    if (devicesPermissions.length) {
      await assignUserToMultipleDevicePermissions(newUserData.name, userData.id, devicesPermissions)
    }
  } catch (error) {
    console.error('Error while saving user data:', error)
    throw error
  }
}

export async function resendUserInvitation (userId) {
  try {
    const db = await firestore
    const userRef = db.collection('app/v1/users').doc(userId)
    await userRef.update({
      resendInvitation: true
    })
  } catch (error) {
    console.error('Error resending user invitation:', JSON.stringify(error))
    throw error
  }
}

/**
* Updates the reports permission of every user in store
* @param {Array<Object>} users - Users who will get the permission updated
* @param {number} value - The new value of the permission
*/
function updateStoreReportsPermission (users, value) {
  const usersIds = users.map(u => u.id)
  const storeUsers = store.getters.organizationUsers
  storeUsers.forEach(user => {
    if (usersIds.includes(user.id)) {
      user.reports = value
    }
  })
  store.commit('setOrganizationUsers', storeUsers)
}

/**
* Updates the store devices, modifieng the permission selected of every user
* @param {Array<Object>} users - Users who will get the permission updated
* @param {string} permission - The name of the notification permission
* @param {number} value - The new value of the permission
*/
function updateStoreNotificationsPermissions (users, permission, value) {
  const usersIds = users.map(u => u.id)
  const devices = store.getters.devices
  devices.forEach(dev => {
    dev.notifications.forEach((noti, index) => {
      if (usersIds.includes(noti.id)) {
        dev.notifications[index].permissions[permission] = value
      }
    })
  })
  store.commit('setDevices', devices)
}

/**
* Updates the selected notification permission of a list of users.
* The permissions are in the devices, so each user should have a list of devices.
* @param {Array<Object>} users - Users who will get the permission updated
* @param {string} permission - The name of the notification permission
* @param {number} value - The new value of the permission
*/
export async function updateUsersNotificationsPermissions (users, permission, value) {
  try {
    const db = await firestore

    if (permission === 'reports') {
      const promises = users.map(user => {
        return db.collection('app/v1/users').doc(user.id).update({reports: value})
      })

      await Promise.all(promises)
      updateStoreReportsPermission(users, value)
    } else {
      const devices = {}
      let dbDevices = []
      users.forEach(user => {
        user.devices.forEach(devId => {
          if (devices[devId]) {
            devices[devId].push({id: user.id, name: user.name})
          } else {
            devices[devId] = [{id: user.id, name: user.name}]
            dbDevices.push(db.collection('app/v1/devices').doc(devId).get())
          }
        })
      })
      dbDevices = await Promise.all(dbDevices)
      const promises = []
      dbDevices.forEach(dbDev => {
        const dbData = dbDev.data()
        let notificationsUsers = dbData.users.permissions.notifications[permission]
        if (value) {
          notificationsUsers = _unionBy(notificationsUsers, devices[dbDev.id], 'id')
        } else {
          notificationsUsers = _differenceBy(notificationsUsers, devices[dbDev.id], 'id')
        }
        promises.push(dbDev.ref.update({[`users.permissions.notifications.${permission}`]: notificationsUsers}))
      })
      await Promise.all(promises)

      updateStoreNotificationsPermissions(users, permission, value)
    }
  } catch (err) {
    console.error('ERROR_UPDATE_NOTIFICATIONS_PERMISSIONS:', err)
    throw err
  }
}

/**
 * It updates multiple devices documents adding/removing user data about notifications of each type.
 * @param {string} userName the name of the user to be added/removed from notifications.
 * @param {string} userId the id of the user.
 * @param {Array<{
 *  id: string,
 *  permissions: { failure: boolean, off: boolean, position: boolean, pressure: boolean }
 * }>} devicesPermissions list of devices permissions by type for the user.
 */
export async function assignUserToMultipleDevicePermissions (userName, userId, devicesPermissions = []) {
  try {
    const db = await firestore

    const storeDevices = _cloneDeep(store.getters.devices)

    await Promise.all(devicesPermissions.map(async (device) => {
      const ref = db.doc(`app/v1/devices/${device.id}`)
      const doc = await ref.get()
      const notifications = _get(doc.data(), 'users.permissions.notifications', {})
      const oldNotifications = _cloneDeep(notifications)
      const newNotifications = _cloneDeep(notifications)
      const storeDevice = storeDevices.find(d => d.id === device.id)
      const storeDeviceNotifications = storeDevice.notifications.find(storeUser => storeUser.id === userId)

      NOTIFICATIONS_TYPES.forEach(type => {
        const users = notifications[type] || []

        newNotifications[type] = device.permissions[type]
          ? _unionBy(users, [{ id: userId, name: userName }], 'id')
          : users.filter(user => user.id !== userId)
      })

      storeDeviceNotifications.permissions = { ...device.permissions }

      await ref.update({ 'users.permissions.notifications': newNotifications })
      await addChangeLog(
        ref,
        { users: { permissions: { notifications: oldNotifications } } },
        { users: { permissions: { notifications: newNotifications } } }
      )
    }))

    store.commit('setDevices', storeDevices)
  } catch (error) {
    console.error('Error while assigning user to multiple devices permissions:', JSON.stringify(error))
    throw error
  }
}

/**
 * It updates a device notifications configuration with multiple users data, that will replace the
 * current stored one.
 * @param {string} deviceId id of the device to be updated
 * @param {Array<{
 *  id: string,
 *  name: string,
 *  permissions: { failure: boolean, off: boolean, position: boolean, pressure: boolean }
 * }>} usersToBeNotifed list of users to be added to the device's notifications configuration.
 */
export async function assigneMultipleUsersToDevicePermissions (deviceId, usersToBeNotifed = []) {
  try {
    const db = await firestore
    const ref = db.doc(`app/v1/devices/${deviceId}`)
    const device = await ref.get()

    const newNotifications = device.data().users.permissions.notifications

    NOTIFICATIONS_TYPES.forEach(type => {
      const users = []

      usersToBeNotifed.forEach(user => {
        const shouldBeNotified = _get(user, `permissions[${type}]`, false)
        if (shouldBeNotified) users.push({ id: user.id, name: user.name })
      })

      newNotifications[type] = users
    })

    await ref.update({ 'users.permissions.notifications': newNotifications })
  } catch (error) {
    console.error('Error while assigning multiple users to device permissions:', JSON.stringify(error))
    throw error
  }
}

async function linkDeviceToOrg (orgRef, devRef) {
  const orgDoc = await orgRef.get()
  let {devices} = orgDoc.data()
  if (devices) {
    // Check if devices already exists in organization devices array
    if (!_some(devices, (elem) => elem.id === devRef.id)) devices.push(devRef)
  } else {
    devices = [devRef]
  }
  return orgRef.update({devices})
}

/**
 * Stores the pluviometric chart file and returns the path of where it was stored
 * @param {File} pluviometricChartFile
 * @param {string} deviceName
 * @returns {Promise<string>}
 */
export async function uploadPluviometricChartFile (pluviometricChartFile, deviceName) {
  const orgName = store.getters.userOrganization.name
  const fileName = pluviometricChartFile.name.split('.')
  const fileType = fileName.pop()
  const parsedFileName = `${fileName.join('_').slice(0, 30)}_${new Date().getTime()}.${fileType}`
  const filePath = `Spray_Letters/${orgName}/${deviceName}/${parsedFileName}`

  await storage.ref().child(filePath).put(pluviometricChartFile)
  return filePath
}

async function updateDevicesStore (devRef) {
  const devDoc = await devRef.get()
  const devData = devDoc.data()
  const parsedData = parseDevice(devRef.id, devData)
  const devices = store.getters.devices
  const devIndex = devices.findIndex(dev => dev.id === parsedData.id)
  if (devIndex >= 0) {
    devices[devIndex] = parsedData
  } else {
    devices.push(parsedData)
  }
  store.commit('setDevices', devices)
}

/**
 * Processes and uploads pluviometric chart file only if it's different from the existing one
 * @param {Object} chartMetadata - Chart metadata containing pluviometricChartFile
 * @param {string|null} existingChartId - ID of existing chart if updating
 * @param {boolean} isNewDevice - Whether this is a new device creation
 * @param {string} deviceName - Name of the device for file path generation
 * @param {Object} db - Firestore database instance
 * @param {Object} transaction - Firestore transaction instance
 * @returns {Promise<string|null>} The file path if uploaded/preserved, null otherwise
 */
async function processPluviometricChartFile (chartMetadata, existingChartId, isNewDevice, deviceName, db, transaction) {
  // If file was already uploaded in the "load chart" flow, reuse that path
  if (chartMetadata.fileAlreadyUploaded && chartMetadata.linkToFile) {
    return chartMetadata.linkToFile
  }

  // Check if a new file was provided (File object with size > 0 indicates a real uploaded file)
  const hasNewFile = chartMetadata.pluviometricChartFile &&
                    chartMetadata.pluviometricChartFile instanceof File &&
                    chartMetadata.pluviometricChartFile.size > 0

  // Get existing chart data if updating (to compare and preserve existing file if needed)
  let existingChartData = null
  if (existingChartId && !isNewDevice) {
    const existingChartRef = db.collection('app/v1/pluviometricCharts').doc(existingChartId)
    const existingChartDoc = await transaction.get(existingChartRef)
    if (existingChartDoc.exists) {
      existingChartData = existingChartDoc.data()
    }
  }

  if (hasNewFile) {
    // If updating an existing chart, check if the file is different from the current one
    let shouldUploadFile = true

    if (existingChartData && existingChartData.linkToFile) {
      const existingFileName = existingChartData.linkToFile.split('/').pop()
      const newFileName = chartMetadata.pluviometricChartFile.name

      // Only upload if the file name is different (indicating a new file was selected)
      shouldUploadFile = existingFileName !== newFileName
    }

    // Upload the file only if it's different from the existing one
    if (shouldUploadFile) {
      const filePath = await uploadPluviometricChartFile(chartMetadata.pluviometricChartFile, deviceName)
      return filePath
    } else if (existingChartData && existingChartData.linkToFile) {
      // Keep the existing file path if the file hasn't changed
      return existingChartData.linkToFile
    }
  } else if (chartMetadata && chartMetadata.linkToFile) {
    // If no new file was provided but chart exists, preserve the existing file
    return chartMetadata.linkToFile
  }

  return null
}

/**
 * Updates only the pluviometricCharts array on an existing device (e.g. before calling the pluviometric chart webhook).
 * Merges the given chart payload at the specified index; reads current array from Firestore first.
 * @param {string} deviceId - Device document ID
 * @param {number} chartIndex - Index in pluviometricCharts to set
 * @param {Object} chartPayload - Chart object to write (e.g. linkToFile, read, validityDate, creationDate, saveDate, isActive)
 * @returns {Promise<void>}
 */
export async function updateDevicePluviometricCharts (deviceId, chartIndex, chartPayload) {
  const db = await firestore
  const deviceRef = db.collection('app/v1/devices').doc(deviceId)
  const doc = await deviceRef.get()
  if (!doc.exists) {
    throw new Error('Device not found')
  }
  const data = doc.data()
  const currentCharts = Array.isArray(data.pluviometricCharts) ? _cloneDeep(data.pluviometricCharts) : []
  while (currentCharts.length <= chartIndex) {
    currentCharts.push({})
  }
  const base = { ...currentCharts[chartIndex] }
  // If payload carries a new linkToFile, clear the old parsedChartReference so the backend does not treat it as "already processed"
  if (chartPayload.linkToFile && chartPayload.linkToFile !== base.linkToFile) {
    delete base.parsedChartReference
  }
  currentCharts[chartIndex] = { ...base, ...chartPayload }
  await deviceRef.update({ pluviometricCharts: currentCharts })
  await updateDevicesStore(deviceRef)
}

/**
 * Creates a minimal draft device in Firestore to obtain a device ID (e.g. before calling pluviometric chart webhook).
 * @param {Object} deviceData - Must have name, type, equipment: { type, subtype? }, serviceStartDate
 * @returns {Promise<string>} The new device ID
 */
export async function createDraftDevice (deviceData) {
  const db = await firestore
  const devicesRef = db.collection('app/v1/devices')
  const deviceName = _get(deviceData, 'name', '')
  const now = new Date()

  const deviceId = await db.runTransaction(async (transaction) => {
    const id = await generateUniqueDeviceId(db, transaction)
    const orgRef = db.collection('app/v1/organizations').doc(store.getters.userOrganization.id)

    const equipment = {
      type: deviceData.type,
      positionNotification: true
    }
    if (deviceData.equipment && deviceData.equipment.type === EQUIPMENT_TYPE.AF && deviceData.equipment.subtype != null) {
      equipment.subtype = deviceData.equipment.subtype
    }

    const queryData = {
      equipment,
      campaign: {
        entityName: deviceName,
        crops: [],
        season: new Date().getFullYear()
      },
      isActive: true,
      organization: orgRef,
      users: {
        data: [],
        permissions: {
          notifications: { failure: [], off: [], position: [], pressure: [] },
          settings: { off: { endTime: now, startTime: now } }
        }
      },
      state: { current: { value: 0 }, previous: {} },
      settings: {},
      offNotification: true,
      pressureNotification: { low: true, high: true }
    }
    if (deviceData.serviceStartDate) {
      queryData.serviceStartDate = deviceData.serviceStartDate
    }

    await Promise.all([
      transaction.set(devicesRef.doc(id), queryData),
      linkDeviceToOrg(orgRef, devicesRef.doc(id))
    ])
    return id
  })

  const activeCampaignId = store.getters.activeCampaign
  if (activeCampaignId) {
    const campaignRef = db.collection('app/v1/campaigns').doc(activeCampaignId)
    const campaignDoc = await campaignRef.get()
    const campaignData = campaignDoc.data()
    if (campaignData) {
      const currentDevices = campaignData.devices || []
      const deviceRef = db.collection('app/v1/devices').doc(deviceId)
      if (!currentDevices.some(device =>
        (typeof device === 'object' && device.id === deviceId) ||
        (typeof device === 'string' && device === deviceId)
      )) {
        await campaignRef.update({ devices: [...currentDevices, deviceRef] })
      }
    }
  }

  await updateDevicesStore(devicesRef.doc(deviceId))
  return deviceId
}

/**
 * Creates or updates a device with its pluviometric charts data
 * @param {Object} deviceData - Device information including name, type, equipment, etc.
 * @param {Object} oldDeviceData - Old device information including name, type, equipment, etc.
 * @param {Array<Object>} oldPluviometricChartsData - Array of old pluviometric chart data objects
 * @param {Array<Object>} pluviometricChartsData - Array of pluviometric chart data objects
 * @param {boolean} isNewDevice - Whether this is a new device creation or an update
 * @returns {Promise<string>} The device ID
 */
export async function upsertDevice (oldDeviceData, deviceData, oldPluviometricChartsData, pluviometricChartsData, isNewDevice) {
  try {
    const db = await firestore
    const devicesRef = db.collection('app/v1/devices')

    const deviceId = await db.runTransaction(async (transaction) => {
      // Extract device metadata and clean up the data structure
      const {name: deviceName, positionNotification, ...cleanDeviceData} = deviceData
      let queryData = {...cleanDeviceData}
      delete queryData.id

      // Process all pluviometric charts
      // Each chart in the array needs to be validated, updated/created, and linked to the device
      const processedPluviometricCharts = []

      if (pluviometricChartsData && pluviometricChartsData.length > 0 &&
          deviceData.pluviometricCharts && deviceData.pluviometricCharts.length > 0) {

        // Iterate through each pluviometric chart data and corresponding chart metadata
        for (let index = 0; index < pluviometricChartsData.length; index++) {
          const chartData = pluviometricChartsData[index]
          const chartMetadata = deviceData.pluviometricCharts[index]

          // Check if chart data has content to save (non-empty object)
          const hasChartData = chartData && Object.keys(chartData).length > 0
          
          if (hasChartData) {
            // Get existing chart ID if available, otherwise it will be created
            const existingChartId = (chartMetadata && chartMetadata.parsedChartId) || null

            // Upsert the pluviometric chart data (creates new or updates existing)
            const oldChartData = oldPluviometricChartsData[index] || {}
            const {chartId} = await upsertPluviometricChartData(oldChartData, chartData, existingChartId)
            deviceData.pluviometricCharts[index].parsedChartId = chartId

            // Build the pluviometric chart object with metadata (fieldSource persists auto/manual per field)
            const formFieldSource = (pluviometricChartsData[index] && pluviometricChartsData[index].fieldSource) || {}
            const pluviometricChart = {
              validityDate: chartMetadata.validityDate ? new Date(chartMetadata.validityDate) : '',
              creationDate: chartMetadata.creationDate ? new Date(chartMetadata.creationDate) : '',
              parsedChartReference: db.collection('app/v1/pluviometricCharts').doc(chartId),
              saveDate: new Date(),
              isActive: chartMetadata.isActive || false,
              read: chartMetadata.read !== false,
              fieldSource: { ...DEFAULT_FIELD_SOURCE, ...(chartMetadata.fieldSource || {}), ...formFieldSource }
            }

            // Process and upload pluviometric chart file (only if different from existing)
            const filePath = await processPluviometricChartFile(
              chartMetadata,
              existingChartId,
              isNewDevice,
              deviceName,
              db,
              transaction
            )
            if (filePath) {
              pluviometricChart.linkToFile = filePath
            }

            processedPluviometricCharts.push(pluviometricChart)
          }
        }
      }

      // Store the array of pluviometric charts if any were processed
      if (processedPluviometricCharts.length > 0) {
        queryData.pluviometricCharts = processedPluviometricCharts
      }

      // Store subtype only if device type is equal to Linear (AF - Avance Frontal)
      if (queryData.equipment.type !== EQUIPMENT_TYPE.AF) {
        delete queryData.equipment.subtype
      }

      let id

      if (isNewDevice) {
        // Generate or use provided device ID for new devices
        id = _get(deviceData, 'id', '')
        if (!id) {
          // Generate unique random device ID if not provided
          id = await generateUniqueDeviceId(db, transaction)
        }

        const orgRef = db.collection('app/v1/organizations').doc(store.getters.userOrganization.id)
        const now = new Date()

        // Build complete device data structure for new devices
        // Includes default campaign, user permissions, state, and notification settings
        queryData = {
          ...queryData,
          equipment: {
            ...queryData.equipment,
            positionNotification: true
          },
          campaign: {
            entityName: deviceName,
            crops: [],
            season: new Date().getFullYear()
          },
          isActive: true,
          organization: orgRef,
          users: {
            data: [],
            permissions: {
              notifications: {
                failure: [],
                off: [],
                position: [],
                pressure: []
              },
              settings: {
                off: {
                  endTime: now,
                  startTime: now
                }
              }
            }
          },
          state: {
            current: {
              value: 0
            },
            previous: {}
          },
          settings: {},
          offNotification: true,
          pressureNotification: {
            low: true,
            high: true
          }
        }

        // Create device and link it to the organization in parallel
        await Promise.all([
          transaction.set(devicesRef.doc(id), queryData),
          linkDeviceToOrg(orgRef, devicesRef.doc(id))
        ])

        cleanPluviometricChartsForLog(queryData)
        await addChangeLog(devicesRef.doc(id), {}, queryData, transaction)
      } else {
        // Update existing device
        id = deviceData.id
        queryData['campaign.entityName'] = deviceName
        queryData.equipment.positionNotification = positionNotification

        // For updates, Firestore requires dot notation for nested fields
        // Convert pluviometricCharts array to update format
        if (processedPluviometricCharts.length > 0) {
          queryData.pluviometricCharts = processedPluviometricCharts
        }

        await transaction.update(devicesRef.doc(id), queryData)

        cleanPluviometricChartsForLog(oldDeviceData)
        cleanPluviometricChartsForLog(deviceData)
        await addChangeLog(
          devicesRef.doc(id),
          oldDeviceData,
          deviceData,
          transaction
        )

        return id
      }
      return id
    })

    // Add device to active campaign if it's a new device
    // This ensures the device appears in the current campaign's device list
    if (isNewDevice) {
      const activeCampaignId = store.getters.activeCampaign
      if (activeCampaignId) {
        const campaignRef = db.collection('app/v1/campaigns').doc(activeCampaignId)
        const campaignDoc = await campaignRef.get()
        const campaignData = campaignDoc.data()

        if (campaignData) {
          const currentDevices = campaignData.devices || []
          const deviceRef = db.collection('app/v1/devices').doc(deviceId)

          // Add device to campaign if not already present
          // Prevents duplicate entries in the campaign's device array
          if (!currentDevices.some(device =>
            (typeof device === 'object' && device.id === deviceId) ||
            (typeof device === 'string' && device === deviceId)
          )) {
            await campaignRef.update({
              devices: [...currentDevices, deviceRef]
            })
          }
        }
      }
    }

    // Update the local store with the latest device data
    await updateDevicesStore(devicesRef.doc(deviceId))
    return deviceId
  } catch (err) {
    console.error('ERROR_UPSERT_DEVICE:', err)
    throw err
  }
}

/**
 * Cleans up pluviometric chart objects inside the given device data for logging purposes.
 * Specifically, it removes the 'pluviometricChartFile' property and any properties whose value is undefined.
 *
 * @param {Object} deviceData - The device data object potentially containing pluviometric charts.
 */
function cleanPluviometricChartsForLog(deviceData) {
  if (deviceData.pluviometricCharts && deviceData.pluviometricCharts.length) {
    deviceData.pluviometricCharts.forEach(chart => {
      // Remove the pluviometricChartFile property
      delete chart.pluviometricChartFile

      // Remove any undefined properties
      Object.keys(chart).forEach(key => {
        if (chart[key] === undefined) {
          delete chart[key]
        }
      })
    })
  }
}

/**
 * Accepts a device service postponement request and updates the device's service start date
 * @param {string} deviceId - The ID of the device to update
 * @param {Date} selectedDate - The new selected date for the service
 * @throws {Error} If there is an error accepting the device postponement
 */
export async function acceptDevicePostponement(deviceId, selectedDate) {
  const campaignId = store.getters.activeCampaign
  try {
    const db = await firestore
    const deviceRef = db.collection('app/v1/devices').doc(deviceId)

    // Update device postponement request
    await deviceRef.update({
      'postponementRequest.selectedDate': selectedDate,
      'postponementRequest.accepted': true,
      'serviceStartDate': selectedDate
    })

    // Update campaign preDataProductsDetail
    const campaignRef = db.collection('app/v1/campaigns').doc(campaignId)
    const campaignDoc = await campaignRef.get()
    const campaignData = campaignDoc.data()
    const currentPreDataProductsDetail = campaignData.preDataProductsDetail || []

    // Find the product and device index
    let productIndex = -1
    let deviceIndex = -1

    _some(currentPreDataProductsDetail, (product, pIndex) => {
      deviceIndex = _findIndex(product.devices, device => device.instanceId === deviceId)
      if (deviceIndex !== -1) {
        productIndex = pIndex
        return true
      }
      return false
    })

    // Update the device start date if found
    if (productIndex !== -1 && deviceIndex !== -1) {
      const formattedDate = `${selectedDate.getDate()}/${selectedDate.getMonth() + 1}/${selectedDate.getFullYear()}`
      currentPreDataProductsDetail[productIndex].devices[deviceIndex].startDate = formattedDate

      await campaignRef.update({
        preDataProductsDetail: currentPreDataProductsDetail
      })

      // Update the device start date in the store
      const storedCampaignData = store.getters.getCampaign(campaignId)
      store.commit('updateCampaign', {
        campaignId,
        campaignData: {
          ...storedCampaignData,
          preDataProductsDetail: currentPreDataProductsDetail
        }
      })
    }

    await updateDevicesStore(deviceRef)
  } catch (err) {
    console.error('ERROR_ACCEPT_DEVICE_POSTPONEMENT:', err)
    throw err
  }
}

export async function createCrop (cropData, campaignId) {
  try {
    const db = await firestore
    let devRefs = []

    cropData.devices.forEach(deviceId => {
      devRefs.push(db.collection('app/v1/devices').doc(deviceId))
    })

    const data = {
      devices: devRefs,
      area: cropData.area,
      name: cropData.name,
      startDate: cropData.sowingDate ? new Date(cropData.sowingDate.split('/').reverse().join('-')) : null,
      type: cropData.type.join('|'),
      field: db.collection('app/v1/fields').doc(cropData.field),
      errorMargin: 5,
      campaign: db.collection('app/v1/campaigns').doc(campaignId),
      organization: db.collection('app/v1/organizations').doc(store.getters.userOrganization.id)
    }

    if (cropData.shapeType) {
      data.shapeType = cropData.shapeType
      if (cropData.shapeType === EQUIPMENT_TYPE.IR) {
        data.points = cropData.points
      } else if (cropData.shapeType === EQUIPMENT_TYPE.AF) {
        data.startCoordinatesString = cropData.startCoordinatesString
        data.startPoint = new firestoreConstructors.GeoPoint(cropData.startPoint.latitude, cropData.startPoint.longitude)
        data.endCoordinatesString = cropData.endCoordinatesString
        data.endPoint = new firestoreConstructors.GeoPoint(cropData.endPoint.latitude, cropData.endPoint.longitude)
      } else {
        data.centerCoordinatesString = cropData.centerCoordinatesString
        data.location = new firestoreConstructors.GeoPoint(cropData.location.latitude, cropData.location.longitude)
        data.startCoordinatesString = cropData.startCoordinatesString
        data.startPoint = new firestoreConstructors.GeoPoint(cropData.startPoint.latitude, cropData.startPoint.longitude)
        data.endCoordinatesString = cropData.endCoordinatesString
        data.endPoint = new firestoreConstructors.GeoPoint(cropData.endPoint.latitude, cropData.endPoint.longitude)
        data.startAngle = getBearing(cropData.location, cropData.startPoint)
        data.endAngle = getBearing(cropData.location, cropData.endPoint)
        data.radius = cropData.radius
      }
    }
    const newCropRef = await db.collection('app/v1/crops').add(data)
    await addChangeLog(newCropRef, {}, data)
    addCropToStoreCrop({...cropData, campaign: campaignId, organization: store.getters.userOrganization.id}, newCropRef.id)
    addCropToStoreField(cropData.field, newCropRef.id)
    addCropToStoreDevices(cropData, newCropRef.id)
  } catch (err) {
    console.error('ERROR_CREATE_CROP:', err)
    throw err
  }
}

export async function updateCrop (oldCrop, newCrop) {
  try {
    const db = await firestore

    let devRefs = []
    newCrop.devices.forEach(deviceId => {
      devRefs.push(db.collection('app/v1/devices').doc(deviceId))
    })
    const newCropObj = {
      ...newCrop,
      devices: devRefs,
      type: newCrop.type.join('|'),
      startDate: newCrop.sowingDate ? new Date(newCrop.sowingDate.split('/').reverse().join('-')) : null,
      field: db.collection('app/v1/fields').doc(newCrop.field),
      campaign: db.collection('app/v1/campaigns').doc(newCrop.campaign),
      organization: db.collection('app/v1/organizations').doc(newCrop.organization),
      clonedFrom: newCrop.clonedFrom && newCrop.clonedFrom.length > 0 ? newCrop.clonedFrom.map(cloned => db.collection('app/v1/crops').doc(cloned)) : []
    }
    delete newCropObj.sowingDate
    delete newCropObj.id

    // Update crop boundary angles if pivot device
    if (newCrop.shapeType === EQUIPMENT_TYPE.PIVOT) {
      newCropObj.startAngle = getBearing(newCrop.location, newCrop.startPoint)
      newCropObj.endAngle = getBearing(newCrop.location, newCrop.endPoint)
    }

    const cropRef = db.collection('app/v1/crops').doc(newCrop.id)
    await cropRef.update(newCropObj)
    await addChangeLog(cropRef, oldCrop, newCrop)

    // update crop into store devices
    updateCropIntoStoreDevices(oldCrop, newCrop)

    if (oldCrop.field !== newCrop.field) { // crop field changed
      updateCropIntoStoreFields(oldCrop, newCrop)
    }

    updateCropIntoStoreCrops(newCrop)
  } catch (err) {
    console.error('ERROR_UPDATE_CROP:', err)
    throw err
  }
}

function updateFieldStore (fieldData) {
  const fields = store.getters.fields
  const index = fields.findIndex(f => f.id === fieldData.id)
  if (index >= 0) {
    fields[index] = fieldData
  } else {
    fields.push(fieldData)
  }
  store.commit('setFields', fields)

  const organizationFields = [...store.getters.organizationFields]
  const orgFieldIndex = organizationFields.findIndex(f => f.id === fieldData.id)
  if (orgFieldIndex >= 0) {
    organizationFields[orgFieldIndex] = fieldData
  } else {
    organizationFields.push(fieldData)
  }
  const filteredOrganizationFields = getLatestClonedFields(organizationFields)
  store.commit('setOrganizationFields', filteredOrganizationFields)
}

export async function upsertField (fieldData, isNewField, campaignId) {
  try {
    const db = await firestore
    const orgId = store.getters.userOrganization.id

    const data = {
      name: fieldData.name,
      contact: db.collection('app/v1/users').doc(fieldData.contact),
      organization: db.collection('app/v1/organizations').doc(orgId),
      gates: fieldData.gates.map(g => new firestoreConstructors.GeoPoint(g.latitude, g.longitude)),
      crops: fieldData.crops.map(cropId => db.collection('app/v1/crops').doc(cropId)),
      campaign: db.collection('app/v1/campaigns').doc(campaignId || store.getters.activeCampaign)
    }

    if (fieldData.auxContact) {
      data.auxContact = db.collection('app/v1/users').doc(fieldData.auxContact)
    }

    let fieldId
    if (!isNewField) {
      await db.collection('app/v1/fields').doc(fieldData.id).update(data)
      fieldId = fieldData.id
    } else {
      const fieldRef = await db.collection('app/v1/fields').add(data)
      fieldId = fieldRef.id
      fieldData.id = fieldId
    }

    // Add field to active campaign if it's a new field
    if (isNewField) {
      const activeCampaignId = campaignId || store.getters.activeCampaign
      if (activeCampaignId) {
        const campaignRef = db.collection('app/v1/campaigns').doc(activeCampaignId)
        const campaignDoc = await campaignRef.get()
        const campaignData = campaignDoc.data()

        if (campaignData) {
          const currentFields = campaignData.fields || []
          const fieldRef = db.collection('app/v1/fields').doc(fieldId)

          // Add field to campaign if not already present
          if (!currentFields.some(field =>
            (typeof field === 'object' && field.id === fieldId) ||
            (typeof field === 'string' && field === fieldId)
          )) {
            await campaignRef.update({
              fields: [...currentFields, fieldRef]
            })
          }
        }
      }
    }

    updateFieldStore(fieldData)
    return fieldId
  } catch (err) {
    console.error('ERROR_UPSERT_FIELD:', err)
    throw err
  }
}

/**
 * It updates the users assigned to a specific device in the local store.
 * @param {userData[]} users list of users to be assigned/unassigned to/from `device`.
 * @param {{id: string}} device device to be updated in local store.
 */
export async function updateAssignedUsers (usersData = [], device) {
  try {
    const updatedDevices = await assignUsersToDevices(
      usersData,
      [{ ...device, isAssigned: true }],
      { appendDevices: true },
      true
    )

    updateUsersAssignmentsInStore(updatedDevices)

    const updatedUsers = usersData.map(user => {
      const storedUser = store.getters.organizationUsers.find(element => element.id === user.id)

      let devices = storedUser.devices || []
      devices = user.isAssigned
        ? _unionBy(devices, [device.id])
        : devices.filter(deviceId => deviceId !== device.id)

      return { ...storedUser, devices }
    })

    store.commit(
      'setOrganizationUsers',
      _unionBy(updatedUsers, store.getters.organizationUsers, 'id').sort(sortByName)
    )
  } catch (error) {
    console.error('Error while updating device users assignment:', JSON.stringify(error))
    throw error
  }
}

/**
 * It generates unique device id string.
 * @param {object} db - Instance of firestore database.
 * @param {object} transaction - Instance of firestore database transaction.
 */
async function generateUniqueDeviceId (db, transaction) {
  const DEVICE_ID_LENGTH = 6
  let randomString
  let isUniqueId = false
  while (!isUniqueId) {
    randomString = randomstring.generate({
      length: DEVICE_ID_LENGTH,
      charset: 'alphanumeric',
      capitalization: 'lowercase'
    })
    const idDoc = await transaction.get(db.collection('app/v1/devices').doc(randomString))
    if (!idDoc.exists) {
      isUniqueId = true
    }
  }
  return randomString
}

/**
 * Updates campaign in both Firestore and store
 * @param {string} campaignId - Campaign ID to update
 * @param {Object} updatedData - Data to update in the campaign
 */
export async function updateCampaign(campaignId, updatedData) {
  try {
    const db = await firestore
    const campaignRef = db.collection('app/v1/campaigns').doc(campaignId)

    // Update in Firestore
    await campaignRef.update(updatedData)

    // Update in store
    updateCampaignInStore(campaignId, updatedData)
  } catch (error) {
    console.error('Error updating campaign:', error)
    throw error
  }
}

/**
 * Creates devices in the store
 * @param {Object[]} devices - Refs of devices to create
 */
export async function createDevicesInStore (devices, db) {
  const devicesData = await Promise.all(devices.map(async deviceRef => {
    const device = await db.collection('app/v1/devices').doc(deviceRef.id).get()
    return parseDevice(deviceRef.id, device.data())
  }))

  const organizationDevices = [...store.getters.organizationDevices, ...devicesData]
  const filteredOrganizationDevices = getLatestClonedDevices(organizationDevices)
  store.commit('setOrganizationDevices', filteredOrganizationDevices)
}

/**
 * Updates campaign in the store located in state.user.campaigns
 * @param {string} campaignId - Campaign ID to update
 * @param {Object} updatedData - Data to update in the campaign
 */
export function updateCampaignInStore(campaignId, updatedData) {
  try {
    const user = store.getters.user
    // Update userCampaigns
    const campaigns = user && user.campaigns ? user.campaigns : []
    const campaignIndex = campaigns.findIndex(campaign => campaign.id === campaignId)

    if (campaignIndex !== -1) {
      // Update existing campaign
      const updatedCampaigns = [...campaigns]
      updatedCampaigns[campaignIndex] = {
        ...updatedCampaigns[campaignIndex],
        ...updatedData
      }

      // Update user with new campaigns array
      const updatedUser = {
        ...user,
        campaigns: updatedCampaigns
      }

      store.commit('setUser', updatedUser)
    } else {
      console.warn(`Campaign with ID ${campaignId} not found in store`)
    }

    // Update availableCampaigns
    const availableCampaigns = store.getters.availableCampaigns
    const availableCampaignIndex = availableCampaigns.findIndex(c => c.id === campaignId)
    if (availableCampaignIndex !== -1) {
      availableCampaigns[campaignIndex] = {
        ...availableCampaigns[campaignIndex],
        ...updatedData
      }
    }
    store.commit('setAvailableCampaigns', availableCampaigns)
  } catch (error) {
    console.error('Error updating campaign in store:', error)
  }
}

/**
 * Creates update data object for campaign based on step and dataToUpdate
 * @param {Object} campaignData - Current campaign data
 * @param {string} step - Current step being completed
 * @param {Object} dataToUpdate - Additional data to update
 * @returns {Object} Update data object
 */
function createUpdateData(campaignData, step, dataToUpdate) {
  const newPreDataSteps = _merge({}, campaignData.preDataSteps, { [step]: true })
  const updateData = { preDataSteps: newPreDataSteps }

  // Add step-specific data
  switch (step) {
    case FIRESTORE_CAMPAIGN_PRE_DATA_STEPS.INTRODUCTION:
      if (dataToUpdate.campaignName) {
        updateData.name = dataToUpdate.campaignName
      }
      break
    case FIRESTORE_CAMPAIGN_PRE_DATA_STEPS.T_AND_C:
      updateData.hubspotOnboardingState = HUBSPOT_ONBOARDING_STATES.ACCEPTED_TYC
      if (dataToUpdate['t&cVersion']) {
        updateData.termsAndConditionsAcceptance = {
          user: store.getters.user.id,
          acceptedAt: new Date(),
          version: dataToUpdate['t&cVersion']
        }
      }
      break
    case FIRESTORE_CAMPAIGN_PRE_DATA_STEPS.FINISH:
      updateData.hubspotOnboardingState = HUBSPOT_ONBOARDING_STATES.CAMPAIGN_CREATED
      break
    case HUBSPOT_ONBOARDING_STATES.COMPLETED_ONBOARDING:
      updateData.hubspotOnboardingState = HUBSPOT_ONBOARDING_STATES.COMPLETED_ONBOARDING
      break
  }

  return updateData
}

/**
 * Creates Firestore update data with proper references
 * @param {Object} updateData - Base update data
 * @param {Object} db - Firestore database instance
 * @returns {Object} Firestore-compatible update data
 */
function createFirestoreUpdateData(updateData, db) {
  const firestoreData = { ...updateData }

  // Convert user ID to Firestore reference if needed
  if (firestoreData.termsAndConditionsAcceptance &&
      firestoreData.termsAndConditionsAcceptance.user &&
      typeof firestoreData.termsAndConditionsAcceptance.user === 'string') {
    db.collection('app/v1/users').doc(firestoreData.termsAndConditionsAcceptance.user)
  }

  return firestoreData
}

export async function completePreDataStep (campaignId, step, dataToUpdate = {}) {
  const db = await firestore
  const campaignRef = db.collection('app/v1/campaigns').doc(campaignId)

  return db.runTransaction(async (transaction) => {
    const campaignDoc = await transaction.get(campaignRef)
    const campaignData = campaignDoc.data()

    // Create base update data
    const updateData = createUpdateData(campaignData, step, dataToUpdate)

    // Handle step-specific logic
    if (step === FIRESTORE_CAMPAIGN_PRE_DATA_STEPS.DEVICES) {
      await createDevicesInStore(campaignData.devices, db)

    }
    updateCampaignInStore(campaignId, updateData)
    // Create Firestore-compatible update data
    const firestoreUpdateData = createFirestoreUpdateData(updateData, db)
    // Update campaign in Firestore
    transaction.update(campaignRef, cleanDeep(firestoreUpdateData))
  })
}

/**
 * Merges preDataProductsDetail arrays with intelligent device array handling
 * @param {Array} currentData - Current preDataProductsDetail array
 * @param {Array} updateData - New data to merge
 * @returns {Array} Merged array
 */
function mergePreDataProductsDetail(currentData, updateData) {
  if (!Array.isArray(updateData)) {
    return currentData
  }

  // If current data is empty, return update data
  if (!Array.isArray(currentData) || currentData.length === 0) {
    return updateData
  }

  // Create a map of current products by some unique identifier (assuming they have an id or name)
  const currentProductsMap = new Map()
  currentData.forEach((product, index) => {
    const key = product.id || product.name || index
    currentProductsMap.set(key, { ...product })
  })

  // Merge update data with current data
  const mergedData = updateData.map((updateProduct, updateIndex) => {
    const key = updateProduct.id || updateProduct.name || updateIndex
    const currentProduct = currentProductsMap.get(key)

    if (currentProduct) {
      // Product exists, merge it
      return mergeProductData(currentProduct, updateProduct)
    } else {
      // New product, add it
      return updateProduct
    }
  })

  // Add any current products that weren't in the update
  currentProductsMap.forEach((currentProduct, key) => {
    const existsInUpdate = updateData.some(updateProduct => {
      const updateKey = updateProduct.id || updateProduct.name
      return updateKey === key
    })

    if (!existsInUpdate) {
      mergedData.push(currentProduct)
    }
  })

  return mergedData
}

/**
 * Merges individual product data, preserving devices array
 * @param {Object} currentProduct - Current product data
 * @param {Object} updateProduct - Update product data
 * @returns {Object} Merged product data
 */
function mergeProductData(currentProduct, updateProduct) {
  const mergedProduct = { ...currentProduct, ...updateProduct }

  // Special handling for devices array - update existing devices instead of adding new ones
  if (updateProduct.devices && Array.isArray(updateProduct.devices)) {
    const currentDevices = currentProduct.devices || []

    // Update existing devices with new data, don't add new ones
    const updatedDevices = currentDevices.map((currentDevice, index) => {
      // Get the corresponding update device by position
      const updateDevice = updateProduct.devices[index]

      if (updateDevice) {
        // Device exists in update, merge the fields
        return { ...currentDevice, ...updateDevice }
      } else {
        // Device not in update, keep as is
        return currentDevice
      }
    })

    mergedProduct.devices = updatedDevices
  }

  return mergedProduct
}

export async function updatePreDataInformation (campaignId, oldData, dataToUpdate) {
  const db = await firestore
  const campaignRef = db.collection('app/v1/campaigns').doc(campaignId)
  // Recursively remove null and undefined properties
  const cleanData = removeNullUndefinedDeep(dataToUpdate)
  store.commit('updateCampaign', {
    campaignId,
    campaignData: {
      ...store.getters.getCampaign(campaignId),
      preDataProductsDetail: cleanData
    }
  })

  await campaignRef.update({preDataProductsDetail: cleanData})
  await addChangeLog(campaignRef, {preDataProductsDetail: oldData}, {preDataProductsDetail: cleanData})
}

export async function updatePreDataInformationEditCampaign (campaignId, oldData, dataToUpdate) {
  const db = await firestore
  const campaignRef = db.collection('app/v1/campaigns').doc(campaignId)

  // Get current campaign data to merge with updates
  const campaignDoc = await campaignRef.get()
  const currentData = campaignDoc.data()
  const currentPreDataProductsDetail = currentData.preDataProductsDetail || []

  // Recursively remove null and undefined properties from update data
  const cleanUpdateData = removeNullUndefinedDeep(dataToUpdate)

  // Merge current data with updates using deep merge strategy
  const mergedData = mergePreDataProductsDetail(currentPreDataProductsDetail, cleanUpdateData)

  // Update in Firestore
  await campaignRef.update({preDataProductsDetail: mergedData})
  await addChangeLog(campaignRef, {preDataProductsDetail: oldData}, {preDataProductsDetail: cleanUpdateData})

  // Update in store
  updateCampaignInStore(campaignId, {preDataProductsDetail: mergedData})
}

function removeNullUndefinedDeep (data) {
  if (Array.isArray(data)) {
    return data.map(item => removeNullUndefinedDeep(item)).filter(item => item !== null && item !== undefined)
  } else if (data && typeof data === 'object') {
    const cleaned = {}
    Object.keys(data).forEach(key => {
      // Skip specific properties that should be removed
      if (['startDateMenu', 'auxStartDate', 'endDateMenu', 'auxEndDate'].includes(key)) {
        return
      }

      const value = removeNullUndefinedDeep(data[key])
      if (value !== null && value !== undefined) {
        cleaned[key] = value
      }
    })
    return Object.keys(cleaned).length > 0 ? cleaned : null
  } else {
    return data === null || data === undefined ? null : data
  }
}

/**
 * Adds a changelog entry comparing old and new data
 * @param {DocumentReference} entityRef - Firestore reference to the entity
 * @param {Object} oldData - Old data to compare and log
 * @param {Object} newData - New data to compare and log
 * @param {Transaction} transaction - Firestore transaction (optional)
 */
export async function addChangeLog (entityRef, oldData, newData, transaction = null, campaignId = null) {
  try {
    const db = await firestore
    const dataChanges = getDataChanges(oldData, newData)

    if (Object.keys(dataChanges).length > 0) {
      const currentUser = store.getters.userAuth.user
      const changeLogRef = entityRef.collection('changeLogs').doc()
      const data = { 
        createdAt: new Date(),
        userRef: currentUser ? db.collection('app/v1/users').doc(currentUser.uid) : null,
        userEmail: currentUser ? currentUser.email : null,
        dataChanges,
        operation: (!oldData || Object.keys(oldData).length === 0) ? 'CREATE' : 'UPDATE',
        campaignId: campaignId || store.getters.activeCampaign || null
      }

      if (transaction) {
        transaction.set(changeLogRef, data)
      } else {
        await changeLogRef.set(data)
      }
    }
  } catch (error) {
    console.error('Error adding changelog:', error)
    throw error
  }
}

/**
 * Adds a changelog entry for a DELETE operation, storing a full snapshot of the entity
 * @param {DocumentReference} entityRef - Firestore reference to the entity
 * @param {Object} entityData - Full snapshot of the entity at the time of deletion
 * @param {string} campaignId - ID of the active campaign to associate the deletion
 */
export async function addDeleteChangeLog (entityRef, entityData, campaignId) {
  try {
    const db = await firestore
    const currentUser = store.getters.userAuth.user
    const changeLogRef = entityRef.collection('changeLogs').doc()
    await changeLogRef.set({
      createdAt: new Date(),
      userRef: currentUser ? db.collection('app/v1/users').doc(currentUser.uid) : null,
      userEmail: currentUser ? currentUser.email : null,
      dataChanges: entityData,
      operation: 'DELETE',
      campaignId: campaignId || null
    })
  } catch (error) {
    console.error('Error adding delete changelog:', error)
    throw error
  }
}



// WEBPACK FOOTER //
// ./src/lib/crudOperations/write.js